import { Box, Theme, Typography } from "@outschool/backpack";
import { SearchFilters } from "@outschool/gql-backend-generated";
import { SearchUseCase } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  ErrorMessage,
  LazyBox,
  TrackedButton
} from "@outschool/ui-components-shared";
import {
  CategoriesSearchComponent,
  useSearchQuery
} from "@outschool/ui-components-website";
import { Container, Link, Space } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../../shared/Env";
import { useCuratedCategoryPageRelatedCarouselsQuery } from "../../hooks/useCuratedCategoryPageQuery";
import { ActivityListing } from "../listings/ActivityListing";
import { ActivityGridLoadingState } from "./CardLoadingStates";
import CuratedWhatIsOutschool from "./curated/CuratedWhatIsOutschool";
import RelatedCategoryGrid from "./RelatedCategoryGrid";

export interface ContentfulRelatedCategories {
  title: string;
  subTitle: string;
  callToActionText: string;
  callToActionUrl: string;
  searchFilters: SearchFilters;
  rowIndex: number;
}

/**
 * Takes a contentful query result and returns a React DOM which:
 * - useQueryWithPreviousData to get listings out of the contentful category filters
 * - display categories according to screen size
 *
 */
export default function RelatedCategories({
  data,
  loading,
  carouselSearchFilters,
  isTutoringCLP
}: {
  data: ReturnType<typeof useCuratedCategoryPageRelatedCarouselsQuery>["data"];
  loading: boolean;
  carouselSearchFilters: SearchFilters;
  isTutoringCLP: boolean;
}) {
  const contentfulCategories = React.useMemo(() => {
    if (data && !loading) {
      // @ts-ignore TS(2531): Object is possibly 'null'.
      return data?.contentfulCategoryPageCollection.items[0].relatedCategoriesCollection.items.map(
        (item, index) => ({
          // @ts-ignore TS(2531): Object is possibly 'null'.
          title: item.title,
          // @ts-ignore TS(2531): Object is possibly 'null'.
          subTitle: item.subtitle,
          // @ts-ignore TS(2531): Object is possibly 'null'.
          callToActionText: item.callToActionText,
          // @ts-ignore TS(2531): Object is possibly 'null'.
          callToActionUrl: item.callToActionUrl,
          searchFilters: {
            q: item?.query,
            multiTermQuery: item?.multiTermQuery?.split(","),
            age: item?.ages?.join(","),
            format: item?.formats?.join(","),
            delivery: item?.deliveryTypes?.join(","),
            gradeLevel: item?.gradeLevel?.join(","),
            pricePerMeetingMin: item?.minPriceCents,
            pricePerMeetingMax: item?.maxPriceCents,
            isAvailable: true,
            adminTags: item?.adminTags?.join(","),
            fundingPrograms: item?.fundingPrograms,
            capacityMin: item?.capacityMin,
            capacityMax: item?.capacityMax,
            ...carouselSearchFilters,
            theme: item?.category?.join(",")
          } as SearchFilters,
          rowIndex: index
        })
      );
    }

    return [];
  }, [carouselSearchFilters, data, loading]);

  const { t } = useTranslation(
    "client\\components\\categories\\RelatedCategoriesCarousels"
  );
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        paddingTop: "1em"
      }}
    >
      {contentfulCategories.map((category, i) => (
        // @ts-ignore TS(2531): Object is possibly 'null'.
        <React.Fragment key={category.title + i}>
          <LazyBox
            placeholder={<ActivityGridLoadingState cards={isMobile ? 3 : 8} />}
            key={category.title}
            sx={{
              paddingBottom: isMobile ? "2em" : "3em"
            }}
          >
            <RelatedCategorySection
              // @ts-ignore TS(2322): Type '{ title: string | null; subTitle: string | n... Remove this comment to see the full error message
              category={category}
              isTutoringCLP={isTutoringCLP}
            />
          </LazyBox>
          {position({
            totalCount: contentfulCategories.length,
            currentIndex: i,
            relativePosition: 0.25
          }) && (
            <Container>
              <CuratedWhatIsOutschool isTutoringCLP={isTutoringCLP} />
            </Container>
          )}
          {position({
            totalCount: contentfulCategories.length,
            currentIndex: i,
            relativePosition: 0.5
          }) &&
            !isTutoringCLP && (
              <>
                <CategoriesSearchComponent heading={t("Find more classes")} />
                <Space y={"xlarge"} />
              </>
            )}
        </React.Fragment>
      ))}
    </Box>
  );
}

/**
 * Position an element relative to the size of a collection.
 *
 * @param Object
 * @param totalCount number Length of the collection being iterated
 * @param currentIndex index of the current iteration.
 * @param relativePosition percentage of where the element will be positioned
 */
export const position = ({
  totalCount,
  currentIndex,
  relativePosition
}: {
  totalCount: number;
  currentIndex: number;
  relativePosition: number;
}) => {
  return Math.floor(relativePosition * totalCount) === currentIndex;
};

function MobileListings({
  row,
  searchQueryResult
}: {
  row: ContentfulRelatedCategories;
  searchQueryResult: ReturnType<typeof useSearchQuery>;
}) {
  const { exactListings, loading, error, searchUid } = searchQueryResult;

  if (loading) {
    return <ActivityGridLoadingState cards={6} />;
  }

  if (error) {
    return (
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
    );
  }

  return (
    <>
      {exactListings.map((listing, index) => (
        <Box
          key={index}
          sx={{
            paddingTop: 24
          }}
        >
          <ActivityListing
            listing={listing}
            openInNewTab
            trackingProperties={{
              order: index,
              category_slug: row.title,
              category_display_strategy: "curated",
              searchUid
            }}
            listingIndex={index}
          />
        </Box>
      ))}
    </>
  );
}

function RelatedCategorySection({
  category,
  isTutoringCLP
}: {
  category: ContentfulRelatedCategories;
  isTutoringCLP: boolean;
}) {
  const isMobile = useIsMobile();

  const limit = isMobile ? 3 : 8;
  const pageLocation = "CURATED_RELATED_CATEGORY_GRID";

  // new educator tile UI in tutoring page requires recent reviews
  const withRecentReviews = category.searchFilters.capacityMax === 1;

  const searchQueryResult = useSearchQuery(
    {
      referrer: document.referrer,
      variables: {
        limit,
        offset: 0,
        pageLocation,
        searchFilters: category.searchFilters,
        skipRating: false,
        withRecentReviews,
        useCase: SearchUseCase.CategoryContent
      }
    },
    true
  );

  const exploreMoreCta = React.useMemo(() => {
    return category.callToActionText && category.callToActionUrl ? (
      <Box
        sx={{
          textAlign: "center"
        }}
      >
        <TrackedButton
          trackingSharedProperties
          trackingName="curated-carousel-cta"
          trackingUniqueId={category.callToActionText}
          component={Link}
          variant="outlined"
          to={category.callToActionUrl}
          sx={{
            whiteSpace: "break-spaces"
          }}
        >
          {category.callToActionText}
        </TrackedButton>
      </Box>
    ) : null;
  }, [category]);

  if (isMobile && !isTutoringCLP) {
    return (
      <>
        <RelatedCategoryTitle category={category} />
        <MobileListings row={category} searchQueryResult={searchQueryResult} />
        <Space y="large" />
        {exploreMoreCta}
        <Space y="large" />
      </>
    );
  }

  return (
    <RelatedCategoryGrid
      category={category}
      limit={limit}
      searchQueryResult={searchQueryResult}
      isTutoringCLP={isTutoringCLP}
    />
  );
}

function RelatedCategoryTitle({
  category
}: {
  category: ContentfulRelatedCategories;
}) {
  return (
    <Container>
      <Box
        sx={(theme: Theme) => ({
          paddingLeft: 0,
          [theme.breakpoints.up("md")]: {
            paddingLeft: 20
          }
        })}
      >
        <Typography emphasized variant="h4">
          {category.title}
        </Typography>
        <Typography
          variant="inherit"
          sx={{
            color: "grey.600",
            size: "22px"
          }}
        >
          {category.subTitle}
        </Typography>
      </Box>
    </Container>
  );
}
